import type { RoleAssignment, RoleKey } from '@myrewards/contracts';
import type { HomeLayout } from './model';

/**
 * Home's layout rules, by role alone. The live Home (`homeQuery`, `homeModel`) and Preview as's
 * "Home will stack" summary and sample Home (APP-059) all read them here, so a preview can't
 * describe a Home the app wouldn't draw.
 */

const holds = (roles: readonly RoleAssignment[], role: RoleKey): boolean =>
  roles.some((a) => a.role === role);

/**
 * The sector panels Home shows (HOME1): Sector Lead sectors first, then a delegate's, each sector
 * once, and only roles that have a sector.
 */
export function homeSectorAssignments(roles: readonly RoleAssignment[]): RoleAssignment[] {
  const order = (role: RoleKey): number => (role === 'sector_lead' ? 0 : 1);
  const scoped = roles
    .filter((a) => (a.role === 'sector_lead' || a.role === 'delegate') && a.sector !== null)
    .sort((a, b) => order(a.role) - order(b.role));
  const seen = new Set<string>();
  return scoped.filter((a) => {
    const id = a.sector?.id ?? '';
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Roles that don't make someone staff: everyone is an Employee, and a manager sees their team. */
const NOT_STAFF: readonly RoleKey[] = ['employee', 'manager'];

/** Finance is this person's only staff role (F1). */
export function financeOnly(roles: readonly RoleAssignment[]): boolean {
  const staff = new Set(roles.map((a) => a.role).filter((role) => !NOT_STAFF.includes(role)));
  return staff.size === 1 && staff.has('finance');
}

/**
 * Which Home a role mix gets: the Stock Admin team opens on its queue (HOME2); a Sector Lead or
 * delegate (HOME1), an Executive (X1) or a pre-approved requester (R1) gets the blended Home with
 * their panels; someone whose only staff role is Finance gets the Spend dashboard under Waiting
 * on you (F1); everyone else gets E1 (APP-054). Finance with any other staff role keeps that
 * role's Home.
 */
export function homeLayout(roles: readonly RoleAssignment[]): HomeLayout {
  if (holds(roles, 'stock_admin')) return 'queue';
  if (homeSectorAssignments(roles).length > 0) return 'blended';
  if (holds(roles, 'executive') || holds(roles, 'executive_delegate')) return 'blended';
  return financeOnly(roles) ? 'finance' : 'employee';
}

/**
 * Roles that put items in Waiting on you: every role the `WaitingItem` contract names, in role
 * order. A System Admin's console work does (QA-040); since APP-232 a manager's Spot decisions
 * and the Spot Committee's nominations to review do too.
 */
export const WAITING_ROLES: readonly RoleKey[] = [
  'employee',
  'manager',
  'sector_lead',
  'delegate',
  'executive_delegate',
  'requester',
  'executive',
  'finance',
  'stock_admin',
  'payroll',
  'system_admin',
  'spot_reviewer',
  'support',
];
